import React from "react";
import { t } from "@auto/services";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import * as api from "../api";
import {
  selectEntity,
  setCommunes,
  setError,
  setLoading,
  setSections,
  setVotingCenters,
  toggleExpanded,
} from "../state";
import { VIEW_CONSTITUENCY } from "../constants";
import { useObservable } from "../hooks";
import { canCreateGeographyEntity, canOpenGeographyContext } from "@src/security/permissions";

import TreeNode from "./TreeNode";

import styles from "../../ElectoralGeographyPage.module.styl";

const ConstituencyTree = ({ gui, state, setState, events }) => {
  const openContextMenu = (event, type, item) => {
    events.contextMenuOpened.publish({ event, type, item });
  };

  const loadBranchAfterSave = React.useCallback(async ({ entity }) => {
    if (state.viewMode !== VIEW_CONSTITUENCY) {
      return;
    }

    setLoading(setState, true);

    try {
      if (entity === "commune" && state.selection.constituency?.gid) {
        setCommunes(setState, await api.loadCommunesByConstituency(state.selection.constituency.gid));
        return;
      }

      if (entity === "section" && state.selection.commune?.gid) {
        setSections(setState, await api.loadSections(state.selection.commune.gid));
        return;
      }

      if (entity === "voting-center" && state.selection.section?.gid) {
        setVotingCenters(setState, await api.loadVotingCenters(state.selection.section.gid));
      }
    } catch (err) {
      setError(setState, err?.message || String(err));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.selection.commune, state.selection.constituency, state.selection.section, state.viewMode]);

  const selectConstituency = React.useCallback(async (constituency) => {
    const wasExpanded = state.expanded.constituency === constituency.gid;

    selectEntity(setState, "constituency", constituency);
    toggleExpanded(setState, "constituency", constituency.gid);

    if (!constituency?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setCommunes(setState, await api.loadCommunesByConstituency(constituency.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.constituency]);

  const selectCommune = React.useCallback(async (commune) => {
    const wasExpanded = state.expanded.commune === commune.gid;

    selectEntity(setState, "commune", commune);
    toggleExpanded(setState, "commune", commune.gid);

    if (!commune?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setSections(setState, await api.loadSections(commune.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.commune]);

  const selectSection = React.useCallback(async (section) => {
    const wasExpanded = state.expanded.section === section.gid;

    selectEntity(setState, "section", section);
    toggleExpanded(setState, "section", section.gid);

    if (!section?.gid || wasExpanded) {
      return;
    }

    setLoading(setState, true);

    try {
      setVotingCenters(setState, await api.loadVotingCenters(section.gid));
    } finally {
      setLoading(setState, false);
    }
  }, [setState, state.expanded.section]);

  const selectVotingCenter = React.useCallback((votingCenter) => {
    selectEntity(setState, "voting-center", votingCenter);
  }, [setState]);

  useObservable(events.constituencySelected, selectConstituency);
  useObservable(events.communeSelected, selectCommune);
  useObservable(events.sectionSelected, selectSection);
  useObservable(events.votingCenterSelected, selectVotingCenter);
  useObservable(events.entitySaved, loadBranchAfterSave);
  useObservable(events.entityDeleted, loadBranchAfterSave);

  return (
    <>
      {canCreateGeographyEntity(gui, "constituency") ? (
        <button
          type="button"
          className={`${styles.treeNode} ${styles.rootAddNode}`}
          onClick={() => events.createRequested.publish({ entity: "constituency" })}
        >
          <span className={styles.treeTwisty} />
          <span className={styles.treeIcon}>
            <AddOutlinedIcon fontSize="small" />
          </span>
          <span className={styles.treeLabel}>{t("geo.addConstituency")}</span>
        </button>
      ) : null}

      {state.data.constituencies.map((constituency) => {
        const constituencyExpanded = state.expanded.constituency === constituency.gid;

        return (
          <TreeNode
            key={constituency.gid}
            type="constituency"
            item={constituency}
            level={0}
            selected={state.selection.constituency?.gid === constituency.gid}
            expanded={constituencyExpanded}
            hasChildren
            onClick={() => events.constituencySelected.publish(constituency)}
            onContextMenu={canOpenGeographyContext(gui, "constituency") ? openContextMenu : null}
          >
            {state.data.communes.map((commune) => {
              const communeExpanded = state.expanded.commune === commune.gid;

              return (
                <TreeNode
                  key={commune.gid}
                  type="commune"
                  item={commune}
                  level={1}
                  selected={state.selection.commune?.gid === commune.gid}
                  expanded={communeExpanded}
                  hasChildren
                  onClick={() => events.communeSelected.publish(commune)}
                  onContextMenu={canOpenGeographyContext(gui, "commune") ? openContextMenu : null}
                >
                  {state.data.sections.map((section) => {
                    const sectionExpanded = state.expanded.section === section.gid;

                    return (
                      <TreeNode
                        key={section.gid}
                        type="section"
                        item={section}
                        level={2}
                        selected={state.selection.section?.gid === section.gid}
                        expanded={sectionExpanded}
                        hasChildren
                        onClick={() => events.sectionSelected.publish(section)}
                        onContextMenu={canOpenGeographyContext(gui, "section") ? openContextMenu : null}
                      >
                        {state.data.votingCenters.map((votingCenter) => (
                          <TreeNode
                            key={votingCenter.gid}
                            type="voting-center"
                            item={votingCenter}
                            level={3}
                            selected={state.selection.votingCenter?.gid === votingCenter.gid}
                            expanded={false}
                            hasChildren={false}
                            onClick={() => events.votingCenterSelected.publish(votingCenter)}
                            onContextMenu={canOpenGeographyContext(gui, "voting-center") ? openContextMenu : null}
                          />
                        ))}
                      </TreeNode>
                    );
                  })}
                </TreeNode>
              );
            })}
          </TreeNode>
        );
      })}
    </>
  );
};

export default ConstituencyTree;
